import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, Question } from "@phosphor-icons/react";

export default function AdminPayments() {
  const [reqs, setReqs] = useState([]);
  const [tab, setTab] = useState("pending");
  const [modal, setModal] = useState(null);
  const [status, setStatus] = useState("approved");
  const [notes, setNotes] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/payments?status=${tab}`);
    setReqs(data);
  };
  useEffect(() => { load(); }, [tab]);

  const submitReview = async () => {
    try {
      await api.post(`/admin/payments/${modal.id}/review`, { status, admin_notes: notes });
      toast.success("Reviewed");
      setModal(null); setNotes(""); setStatus("approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Payment Requests</h1>
        <div className="flex gap-1 rounded-full bg-slate-100 dark:bg-slate-800 p-1">
          {["pending", "approved", "rejected"].map((t) => (
            <button key={t} data-testid={`admin-payments-tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-1.5 rounded-full text-sm font-bold capitalize transition-colors duration-200 ${tab === t ? "bg-blue-700 text-white" : "text-slate-600 dark:text-slate-300"}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {reqs.length === 0 && <div className="rounded-2xl border-2 border-dashed p-8 text-center text-slate-500">No {tab} payments.</div>}
        {reqs.map((r) => (
          <div key={r.id} data-testid={`admin-payment-${r.id}`} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[240px]">
              <div className="font-bold text-slate-900 dark:text-white">@{r.username} • ${r.amount_usd}</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">{r.kind} {r.credits_requested ? `• ${r.credits_requested} credits` : ""}</div>
              <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">TX: <span className="font-mono">{r.transaction_ref}</span></div>
              {r.notes && <div className="text-xs text-slate-500 mt-1">Notes: {r.notes}</div>}
            </div>
            {r.screenshot_url && <a href={`${backend}${r.screenshot_url}`} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline">View screenshot</a>}
            <div className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</div>
            {r.status === "pending" ? (
              <button data-testid={`admin-review-${r.id}`} onClick={() => setModal(r)} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-4 py-2 text-sm transition-colors duration-200">Review</button>
            ) : (
              <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full ${r.status === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{r.status}</span>
            )}
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-black text-lg text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Review — @{modal.username}</h2>
            <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">${modal.amount_usd} • {modal.kind}</div>
            <div className="mt-3 flex gap-2">
              {[["approved", "Approve", CheckCircle, "bg-green-600"], ["rejected", "Reject", XCircle, "bg-red-500"], ["info_requested", "Ask info", Question, "bg-yellow-500"]].map(([v, l, Ic, bg]) => (
                <button key={v} data-testid={`review-${v}`} onClick={() => setStatus(v)} className={`flex-1 rounded-full font-bold px-3 py-2 text-sm text-white inline-flex items-center justify-center gap-1 transition-colors duration-200 ${status === v ? bg : "bg-slate-300 dark:bg-slate-700"}`}>
                  <Ic size={14} weight="fill" /> {l}
                </button>
              ))}
            </div>
            <textarea data-testid="review-notes-input" rows="3" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full mt-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="rounded-full bg-slate-200 dark:bg-slate-700 px-4 py-2 text-sm">Cancel</button>
              <button data-testid="review-submit-btn" onClick={submitReview} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-4 py-2 text-sm transition-colors duration-200">Submit</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
